import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UsersIcon,
  CheckBadgeIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useModal } from '../hooks/useModal';
import { useTableQuery } from '../hooks/useTableQuery';
import { useCrud } from '../hooks/useCrud';
import { useAsideContext } from '@contexts/AsideContext';
import { PageMeta, TableCard, ColumnDefinition } from '@common/components';
import { Button, Badge, Modal, ConfirmDialog, Input, Select, Switch, StatTile } from '@common/components/ui';
import adminUserService from '@services/adminUserService';
import { Admin } from '@services/core/types';
import toast from 'react-hot-toast';

interface AdminFormData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MODERATOR';
  isActive: boolean;
}

interface AdminUserFormProps {
  admin: Admin | null;
  onSave: (data: AdminFormData) => Promise<void>;
  isSaving: boolean;
}

const AdminUserForm = ({ admin, onSave, isSaving }: AdminUserFormProps) => {
  const [formData, setFormData] = useState<AdminFormData>({
    username: admin?.username || '',
    email: admin?.email || '',
    firstName: admin?.firstName || '',
    lastName: admin?.lastName || '',
    password: '',
    role: admin?.role || 'MODERATOR',
    isActive: admin?.isActive ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!admin && !formData.password) {
      toast.error('Password is required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
        <div className="space-y-4">
          <Input
            label="Username"
            required
            value={formData.username}
            onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
          />
          <Input
            label="Email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          />
          <Input
            label="First Name"
            value={formData.firstName}
            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
          />
          <Input
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
          />

          {!admin && (
            <Input
              label="Password"
              type="password"
              required
              minLength={8}
              value={formData.password}
              onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
              helperText="Minimum 8 characters"
            />
          )}

          <Select
            label="Role"
            required
            value={formData.role}
            onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value as AdminFormData['role'] }))}
            options={[
              { value: 'MODERATOR', label: 'Moderator' },
              { value: 'ADMIN', label: 'Admin' },
              { value: 'SUPER_ADMIN', label: 'Super Admin' },
            ]}
          />

          <Switch
            label="Active"
            checked={formData.isActive}
            onCheck={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
          />
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="submit" loading={isSaving}>
          {admin ? 'Update Admin' : 'Create Admin'}
        </Button>
      </div>
    </form>
  );
};

interface ChangePasswordFormProps {
  admin: Admin | null;
  onSuccess: () => void;
}

const ChangePasswordForm = ({ admin, onSuccess }: ChangePasswordFormProps) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      if (!admin) throw new Error('No admin selected');
      if (password !== confirmPassword) throw new Error('Passwords do not match');
      if (password.length < 8) throw new Error('Password must be at least 8 characters');
      return adminUserService.changePassword(admin.id, password);
    },
    onSuccess: () => {
      toast.success('Password changed successfully');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || error.message || 'Failed to change password');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    changePasswordMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Change password for <strong>{admin?.username}</strong>
      </p>

      <Input
        label="New Password"
        type="password"
        required
        minLength={8}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        helperText="Minimum 8 characters"
      />

      <Input
        label="Confirm Password"
        type="password"
        required
        minLength={8}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={password && confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
      />

      <div className="flex gap-3 justify-end pt-4">
        <Button
          type="submit"
          loading={changePasswordMutation.isPending}
          disabled={password !== confirmPassword || !password}
        >
          Change Password
        </Button>
      </div>
    </form>
  );
};

const AdminUsers = () => {
  const queryClient = useQueryClient();
  const { openAside } = useAsideContext();
  const { isOpen: isDeleteOpen, open: openDelete, close: closeDelete } = useModal();
  const { isOpen: isPasswordOpen, open: openPassword, close: closePassword } = useModal();

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null);
  const [adminForPassword, setAdminForPassword] = useState<Admin | null>(null);

  const { handleCreate, handleEdit, handleDelete, isSubmitting } = useCrud<AdminFormData, Admin>({
    queryKey: 'admin-users',
    service: {
      create: (data) => adminUserService.create(data),
      update: (id, data) => adminUserService.update(String(id), data),
      remove: (id) => adminUserService.remove(String(id)),
    },
    messages: {
      created: 'Admin user created successfully',
      updated: 'Admin user updated successfully',
      deleted: 'Admin user deleted successfully',
    },
  });

  const invalidateStats = () => queryClient.invalidateQueries({ queryKey: ['admin-user-stats'] });

  // Fetch admin users
  const { data: adminsData, isLoading, error } = useTableQuery({
    queryKey: ['admin-users', currentPage, pageSize, searchQuery],
    fetchFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(currentPage));
      params.set('limit', String(pageSize));
      if (searchQuery) params.set('q', searchQuery);
      return adminUserService.getAll(params);
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['admin-user-stats'],
    queryFn: () => adminUserService.getStats(),
  });

  const openAdminForm = (admin: Admin | null) => {
    openAside(
      <AdminUserForm
        admin={admin}
        isSaving={isSubmitting}
        onSave={async (data) => {
          if (admin) {
            const { password, ...updateData } = data;
            await handleEdit(admin.id, updateData);
          } else {
            await handleCreate(data);
          }
          invalidateStats();
        }}
      />,
      { title: admin ? 'Edit Admin User' : 'Add Admin User' }
    );
  };

  const handleDeleteClick = (admin: Admin) => {
    setAdminToDelete(admin);
    openDelete();
  };

  const handlePasswordClick = (admin: Admin) => {
    setAdminForPassword(admin);
    openPassword();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'error';
      case 'ADMIN':
        return 'info';
      case 'MODERATOR':
        return 'warning';
      default:
        return 'gray';
    }
  };

  const admins = adminsData?.data.items || [];
  const total = adminsData?.data.total || 0;
  const totalPages = adminsData?.data.totalPages || 1;

  const columns: ColumnDefinition<Admin>[] = [
    { name: 'username', title: 'Username', value: (admin) => <span className="font-medium text-gray-900 dark:text-white">{admin.username}</span> },
    {
      name: 'name',
      title: 'Name',
      value: (admin) => (admin.firstName || admin.lastName ? `${admin.firstName || ''} ${admin.lastName || ''}`.trim() : '-'),
    },
    { name: 'email', title: 'Email', value: (admin) => admin.email },
    {
      name: 'role',
      title: 'Role',
      value: (admin) => <Badge color={getRoleBadgeColor(admin.role)}>{admin.role.replace('_', ' ')}</Badge>,
    },
    {
      name: 'status',
      title: 'Status',
      value: (admin) => <Badge color={admin.isActive ? 'success' : 'gray'}>{admin.isActive ? 'Active' : 'Inactive'}</Badge>,
    },
    { name: 'lastLogin', title: 'Last Login', value: (admin) => (admin.lastLoginAt ? formatDate(admin.lastLoginAt) : 'Never') },
  ];

  return (
    <>
      <PageMeta
        title="Admin Users"
        breadcrumbs={[{ label: 'Admin Users' }]}
        action={
          <Button onClick={() => openAdminForm(null)} icon={<PlusIcon className="h-5 w-5" />}>
            Add Admin
          </Button>
        }
      />

      <div className="space-y-6">
        {/* Stats */}
        {stats?.data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatTile label="Total Admins" value={stats.data.total} icon={<UsersIcon />} iconClassName="bg-brand-500" />
            <StatTile label="Active" value={stats.data.active} icon={<CheckBadgeIcon />} iconClassName="bg-green-500" />
            <StatTile label="Super Admins" value={stats.data.superAdmins} icon={<ShieldExclamationIcon />} iconClassName="bg-danger-500" />
            <StatTile label="Admins" value={stats.data.admins} icon={<ShieldCheckIcon />} iconClassName="bg-blue-500" />
            <StatTile label="Moderators" value={stats.data.moderators} icon={<UserGroupIcon />} iconClassName="bg-yellow-500" />
          </div>
        )}

        <TableCard
          columns={columns}
          data={admins}
          loading={isLoading}
          error={error ? 'Failed to load admin users' : undefined}
          emptyMessage="No admin users found"
          searchable
          searchPlaceholder="Search by username, email, or name..."
          onSearch={(query) => {
            setSearchQuery(query);
            setCurrentPage(1);
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
          onRowClick={openAdminForm}
          rowActions={(admin) => (
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePasswordClick(admin);
                }}
                className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                title="Change Password"
              >
                <KeyIcon className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openAdminForm(admin);
                }}
                className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                title="Edit Admin"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(admin);
                }}
                className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300"
                title="Delete Admin"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        />
      </div>

      {/* Change Password Modal */}
      <Modal
        isOpen={isPasswordOpen}
        onClose={() => {
          closePassword();
          setAdminForPassword(null);
        }}
        title="Change Password"
      >
        <ChangePasswordForm
          admin={adminForPassword}
          onSuccess={() => {
            closePassword();
            setAdminForPassword(null);
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          closeDelete();
          setAdminToDelete(null);
        }}
        onConfirm={async () => {
          if (!adminToDelete) return;
          await handleDelete(adminToDelete.id);
          invalidateStats();
          setAdminToDelete(null);
        }}
        title="Delete Admin User"
        description={`Are you sure you want to delete admin user "${adminToDelete?.username}"? This action cannot be undone.`}
        confirmLabel="Delete"
        successMessage="Admin user deleted"
      />
    </>
  );
};

export default AdminUsers;
